import { FC, ReactNode, createContext, useContext, useState, useEffect, useCallback } from 'react';
import progression_service, { ISeason, ITier } from '../services/progression_service';
import { useUserContext } from './user_context';

interface ProgressionContextType {
    tiers: ITier[];
    seasons: ISeason[];
}

export const ProgressionContext = createContext<ProgressionContextType>({
    tiers: [],
    seasons: [],
});

export const ProgressionProvider: FC<{ children?: ReactNode }> = ({ children }) => {
    const [tiers, set_tiers] = useState<ITier[]>([]);
    const [seasons, set_seasons] = useState<ISeason[]>([]);
    const { access_token } = useUserContext();

    const get_tiers = useCallback(async () => {
        if (!access_token) return false;
        try {
            const tiers = await progression_service.get_tiers(access_token);
            if (tiers) {
                set_tiers(tiers);
                return true;
            }
        } catch (err) {
            console.error('failed to get tiers', err);
        }
        return false;
    }, [access_token]);

    const get_seasons = useCallback(async () => {
        if (!access_token) return false;
        try {
            const seasons = await progression_service.get_seasons(access_token);
            if (seasons) {
                set_seasons(seasons);
                return true;
            }
        } catch (err) {
            console.error('failed to get seasons', err);
        }
        return false;
    }, [access_token]);

    useEffect(() => {
        get_tiers();
        get_seasons();
    }, [get_tiers, get_seasons]);

    return (
        <ProgressionContext.Provider
            value={{
                tiers,
                seasons,
            }}
        >
            {children}
        </ProgressionContext.Provider>
    );
};

export const useProgressionContext = (): ProgressionContextType => {
    const context = useContext(ProgressionContext);
    if (context === undefined) {
        throw new Error('ProgressionContext must be used within a ProgressionProvider');
    }
    return context;
};
